/**
 * Chat API Service
 *
 * Handles chat sessions, messages, documents, and prompt improvement (Tasks 98-116)
 * Task 216: Added matter-scoped chat session methods
 */

import { apiClient, getAuthHeader, handleUnauthorized } from './client';
import { streamChatSend } from './chatStream';
import type { ChatStreamHandlers } from './chatStream';
import { config } from '../../utils/config';
import type { PromptImprovementData } from '../../types/promptImprovement';
import type { ChatMentionRef, ChatStructuredMetadata } from '../../types/chat';

/** Client timeout for POST /chat/sessions/{id}/messages (issue #1236). */
export const CHAT_SEND_TIMEOUT_MS = 120_000;

/**
 * Issue #784 — typed errors for the two `IdempotencyMiddleware` 409 responses.
 *
 * Backend middleware: `backend/app/middleware/idempotency.py:192-213`.
 * Detail strings here MUST match the exact strings emitted by the middleware
 * at lines 195 and 207 — they're the only way to distinguish the two cases
 * on the client. IF YOU CHANGE EITHER STRING, ALSO UPDATE THE MIDDLEWARE.
 */
export class IdempotencyInFlightError extends Error {
  constructor() {
    super('Duplicate request in flight');
    this.name = 'IdempotencyInFlightError';
  }
}

export class IdempotencyDifferentBodyError extends Error {
  constructor(public readonly detail: string) {
    super(detail);
    this.name = 'IdempotencyDifferentBodyError';
  }
}

// Task 216: Types for matter-scoped chat sessions
export interface MatterChatSession {
  id: string;
  user_id: string;
  matter_id: string;
  name: string;
  title?: string;
  created_at: string;
  updated_at: string;
  // Plan 143: first-class scope metadata on chat_sessions.
  scope?: 'general' | 'issue' | 'finding' | 'clause';
  source_id?: string | null;
}

export interface MatterChatSessionsResponse {
  matter_id: string;
  sessions: MatterChatSession[];
  total_count: number;
}

// Plan 174 (#763): bucketed scoped-chat user-turn counts for a matter.
// Returned by GET /api/v1/chat/matter/{matter_id}/scoped-message-counts.
// Each bucket maps source_id (clauseId / issueId / findingId) → count.
export interface ScopedChatCounts {
  clause: Record<string, number>;
  issue: Record<string, number>;
  finding: Record<string, number>;
}

// Plan 150 §7.3: assistant message can carry the dual-section structured
// payload alongside the canonical rendered markdown in `content`. Legacy /
// matter-wide messages have `structured_metadata: null` and the UI falls
// back to rendering `content`.
export interface ChatMessagePayload {
  id: string;
  session_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  citations?: any[] | null;
  created_at: string;
  turn_index?: number | null;
  attached_clauses?: Array<{ clause_title: string; document_name: string }> | null;
  structured_metadata?: ChatStructuredMetadata | null;
}

// Plan 198a: rename endpoint accepts title (ad-hoc sidebar reads this)
// and/or name (matter sidebar reads this). At least one required.
export type ChatSessionRenameOpts = { title?: string; name?: string };

export interface SendChatMessageResponse {
  user_message: ChatMessagePayload;
  assistant_message: ChatMessagePayload;
  // Plan 108: matter-wide playbook injection metadata (independent of
  // mentioned_refs scoping per locked decision #13).
  playbook_citations?: any[];
}

export interface SendChatMessageOptions {
  use_improved_prompt?: boolean;
  original_prompt?: string;
  improved_prompt?: string;
  prompt_improvement_data?: any;
  // Task 219: Filter RAG search to specific document (for quick actions)
  focus_document_id?: string;
  playbook_id?: string;  // Plan 108: Playbook context injection
  // Plan 143: bracketed legal context for scoped chat. Stored alongside the
  // user message but NOT replayed on future turns.
  contextInjection?: string;
  // Plan 150 §7.1: structured @-mentions restricting RAG retrieval. Backend
  // rejects mixed payloads (mentioned_refs + focus_document_id) with HTTP
  // 400; quick-action callers must omit this field.
  mentionedRefs?: ChatMentionRef[];
  // Plan 150 §4.1: set ONLY when the user clicks "Search the whole matter
  // instead" on a zero-result scoped response. Carries the assistant
  // message id of the original scoped reply so the audit row gets
  // widen_accepted=true via the predicated UPDATE in the router.
  widenFromMessageId?: string;
  // Plan 159: set ONLY when the user clicks "Search your attached
  // precedents instead" on a matter-empty + workbench-non-empty
  // zero-result response. The server resolves attached workbench
  // docs server-side (bypassing per-message mention caps); the
  // widenFromMessageId continues to flip widen_accepted=true on the
  // prior audit row. Mutually exclusive with mentioned_refs and
  // focus_document_id at the Pydantic layer.
  widenToAttachedWorkbench?: boolean;
  // Issue #784: opt-in idempotency. When set, sent as the `Idempotency-Key`
  // HTTP header and routed through the existing IdempotencyMiddleware
  // (backend/app/middleware/idempotency.py). MUST NOT land in the request
  // body.
  idempotencyKey?: string;
}

const sendMessagePath = (sessionId: string) => `/api/v1/chat/sessions/${sessionId}/messages`;

/** The request body both send transports use, so they cannot drift. */
function buildSendMessageBody(
  content: string,
  options: Omit<SendChatMessageOptions, 'idempotencyKey'>,
): Record<string, unknown> {
  const { contextInjection, mentionedRefs, widenFromMessageId, widenToAttachedWorkbench, ...rest } = options;
  const body: Record<string, unknown> = { content, ...rest };
  if (contextInjection) {
    body.context_injection = contextInjection;
  }
  if (mentionedRefs && mentionedRefs.length > 0) {
    // Strip frontend-only `label` — backend ChatMentionRef is `{type, id}`.
    body.mentioned_refs = mentionedRefs.map(({ type, id }) => ({ type, id }));
  }
  if (widenFromMessageId) {
    body.widen_from_message_id = widenFromMessageId;
  }
  if (widenToAttachedWorkbench) {
    body.widen_to_attached_workbench = true;
  }
  return body;
}

class ChatAPI {
  // Chat Session Management
  async createChatSession(title?: string, matterId?: string, name?: string): Promise<any> {
    const response = await apiClient.post('/api/v1/chat/sessions', {
      title,
      matter_id: matterId,
      name,
    });
    return response.data;
  }

  async getChatSessions(
    limit: number = 20,
    offset: number = 0,
    matterId?: string,
    adHocOnly?: boolean,
  ): Promise<any> {
    // Plan 181 (#774): adHocOnly restricts to matter_id IS NULL + general
    // scope only. Mutually exclusive with matterId; server returns 400 if
    // both are sent. Caller is responsible for not passing both.
    let url = `/api/v1/chat/sessions?limit=${limit}&offset=${offset}`;
    if (matterId) {
      url += `&matter_id=${matterId}`;
    }
    if (adHocOnly) {
      url += '&ad_hoc_only=true';
    }
    const response = await apiClient.get(url);
    return response.data;
  }

  // Task 216: Matter-scoped chat session methods
  async getMatterChatSessions(matterId: string): Promise<MatterChatSessionsResponse> {
    const response = await apiClient.get(`/api/v1/matters/${matterId}/chat-sessions`);
    return response.data;
  }

  async createMatterChatSession(
    matterId: string,
    name?: string,
    scope?: 'general' | 'issue' | 'finding' | 'clause',
    sourceId?: string,
  ): Promise<MatterChatSession> {
    const body: Record<string, unknown> = { name: name || 'General' };
    // Plan 143: scope metadata — backend make-or-return-existing is idempotent
    // for non-general scopes. General sessions must not carry source_id.
    if (scope && scope !== 'general') {
      body.scope = scope;
      body.source_id = sourceId;
    }
    const response = await apiClient.post(`/api/v1/matters/${matterId}/chat-sessions`, body);
    return response.data;
  }

  // Plan 143: fast-path lookup for scoped sessions used by useScopedChatSession.
  async getChatSessionByScope(
    matterId: string,
    scope: 'issue' | 'finding' | 'clause',
    sourceId: string,
  ): Promise<MatterChatSession | null> {
    try {
      const response = await apiClient.get(
        `/api/v1/matters/${matterId}/chat-sessions/by-scope`,
        { params: { scope, source_id: sourceId } },
      );
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  async getChatSession(sessionId: string): Promise<any> {
    const response = await apiClient.get(`/api/v1/chat/sessions/${sessionId}`);
    return response.data;
  }

  async updateChatSession(
    sessionId: string,
    opts: ChatSessionRenameOpts,
  ): Promise<any> {
    if (!opts.title && !opts.name) {
      throw new Error('updateChatSession: provide title or name');
    }
    const response = await apiClient.put(`/api/v1/chat/sessions/${sessionId}`, opts);
    return response.data;
  }

  async deleteChatSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/api/v1/chat/sessions/${sessionId}`);
  }

  // Chat Messaging
  async sendChatMessage(sessionId: string, content: string, options?: SendChatMessageOptions): Promise<any> {
    // Issue #784: explicit destructure including `idempotencyKey` so it does
    // NOT fall into the body. The middleware hashes the raw body bytes;
    // keeping the key out of those bytes prevents header/body coupling.
    const { idempotencyKey, ...bodyOptions } = options || {};
    const body = buildSendMessageBody(content, bodyOptions);
    // Issue #1236: a reply near CLAUDE_MAX_TOKENS (4000) takes 50-90 s to
    // generate, so the 60 s client default gave up on replies the server
    // then persisted. RightPanelChat recovers a reply that still overruns.
    const requestConfig: Record<string, unknown> = { timeout: CHAT_SEND_TIMEOUT_MS };
    if (idempotencyKey) {
      requestConfig.headers = { 'Idempotency-Key': idempotencyKey };
    }
    try {
      const response = await apiClient.post<SendChatMessageResponse>(
        sendMessagePath(sessionId),
        body,
        requestConfig,
      );
      return response.data;
    } catch (err: any) {
      // Issue #784: distinguish the two middleware 409 responses by detail
      // string. The detail strings come from idempotency.py:195 ("already in
      // progress") and idempotency.py:207 ("already used with a different
      // request body"). If those strings ever change in the middleware, the
      // chatApi.idempotencyHeader.test.ts assertions will fail loud.
      if (err?.response?.status === 409) {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') {
          if (detail.includes('already in progress')) {
            throw new IdempotencyInFlightError();
          }
          if (detail.includes('already used with a different request body')) {
            throw new IdempotencyDifferentBodyError(detail);
          }
        }
      }
      throw err;
    }
  }

  /**
   * Plan 254 (#398): the same send, with the reply streamed as it is written.
   *
   * Resolves with the same response as `sendChatMessage`. No `idempotencyKey`:
   * the server refuses a streamed send that carries one (a failed or
   * truncated stream is still a 200, which the middleware would cache).
   * Callers MUST use `sendChatMessage` for sends with `mentionedRefs` or
   * `widenToAttachedWorkbench` -- the server answers those with JSON anyway.
   */
  async sendChatMessageStream(
    sessionId: string,
    content: string,
    options: Omit<SendChatMessageOptions, 'idempotencyKey'> | undefined,
    handlers: ChatStreamHandlers,
  ): Promise<SendChatMessageResponse> {
    return streamChatSend({
      url: `${config.apiUrl}${sendMessagePath(sessionId)}`,
      body: buildSendMessageBody(content, options || {}),
      handlers,
      authHeader: getAuthHeader,
      onUnauthorized: (failed) => handleUnauthorized(failed),
    });
  }


  async getChatMessages(sessionId: string, limit: number = 100, offset: number = 0): Promise<any> {
    // Issue #707: default `limit` is 100 to match the server-side cap.
    // The endpoint returns the NEWEST `limit` messages (ascending order)
    // and echoes `cap` + `soft_warn_threshold` for the banner UX.
    const response = await apiClient.get(
      `/api/v1/chat/sessions/${sessionId}/messages?limit=${limit}&offset=${offset}`
    );
    return response.data;
  }

  async getMessageCitations(messageId: string): Promise<any> {
    const response = await apiClient.get(`/api/v1/chat/citations/${messageId}`);
    return response.data;
  }

  // Plan 174 (#763): per-source user-turn counts for scoped chats in a matter.
  // One call per matter visit; consumed by ClauseCard / SectionCard badges.
  async getScopedChatMessageCounts(matterId: string): Promise<ScopedChatCounts> {
    const response = await apiClient.get<ScopedChatCounts>(
      `/api/v1/chat/matter/${matterId}/scoped-message-counts`,
    );
    return response.data;
  }

  // Prompt Improvement (Plan 148, session-aware Plan 149)
  async improveChatPrompt(
    prompt: string,
    sessionId?: string,
  ): Promise<PromptImprovementData> {
    const response = await apiClient.post<PromptImprovementData>(
      '/api/v1/chat/improve-prompt',
      { prompt, session_id: sessionId },
    );
    return response.data;
  }

  // Task 222: Silent clause analysis for quick actions
  async analyzeClause(
    sessionId: string,
    request: {
      prompt: string;
      clause_id?: string;
      clause_text?: string;
      action_type: string;
      document_id?: string;
    }
  ): Promise<{
    content: string;
    action_type: string;
    clause_id?: string;
    citations: Array<{
      document_name: string;
      page_number?: number;
      clause_reference?: string;
      text_snippet: string;
    }>;
  }> {
    const response = await apiClient.post(
      `/api/v1/chat/sessions/${sessionId}/analyze`,
      request,
      { timeout: 60000 }
    );
    return response.data;
  }
}

export const chatApi = new ChatAPI();
